import { Controller } from "@hotwired/stimulus";
import Fuse from "fuse.js";

export default class extends Controller {
    static targets = ["input", "group", "item", "empty", "content"];
    static values = { open: Boolean };

    connect() {
        // Only initialize if input target exists
        if (this.hasInputTarget) {
            this.inputTarget.focus();
            this.searchIndex = this.buildSearchIndex();
            this.toggleVisibility(this.emptyTargets, false);
            this.selectedIndex = -1;
        }

        if (this.openValue) {
            this.open();
        }
    }

    open(e) {
        if (e) e.preventDefault();
        document.body.insertAdjacentHTML("beforeend", this.contentTarget.innerHTML);
        document.body.classList.add("tw-overflow-hidden");

        // Re-initialize after content is added to DOM
        this.initializeDialog();
    }

    initializeDialog() {
        // Wait for next tick to ensure DOM is updated
        setTimeout(() => {
            const dialogElement = document.querySelector('[data-controller*="ruby-ui--command"]');
            if (dialogElement) {
                const inputEl = dialogElement.querySelector('[data-ruby-ui--command-target="input"]');
                if (inputEl) {
                    inputEl.focus();
                    this.searchIndex = this.buildSearchIndex();
                    this.selectedIndex = -1;
                }
            }
        }, 0);
    }

    dismiss() {
        document.body.classList.remove("tw-overflow-hidden");
        this.element.remove();
    }

    filter(e) {
        if (!this.hasItemTarget) return;

        this.deselectAll();
        const query = e.target.value.toLowerCase();

        if (!query.length) return this.resetVisibility();

        this.toggleVisibility(this.itemTargets, false);
        const results = this.searchIndex.search(query);
        results.forEach((r) => this.toggleVisibility([r.item.element], true));
        this.toggleVisibility(this.emptyTargets, results.length === 0);
        this.updateGroupVisibility();
    }

    toggleVisibility(elements, show) {
        elements.forEach((el) => el.classList.toggle("tw-hidden", !show));
    }

    updateGroupVisibility() {
        if (!this.hasGroupTarget) return;

        this.groupTargets.forEach((group) => {
            const hasVisible = group.querySelectorAll("[data-ruby-ui--command-target='item']:not(.tw-hidden)").length > 0;
            this.toggleVisibility([group], hasVisible);
        });
    }

    resetVisibility() {
        if (!this.hasItemTarget) return;

        this.toggleVisibility(this.itemTargets, true);
        this.toggleVisibility(this.groupTargets, true);
        this.toggleVisibility(this.emptyTargets, false);
    }

    buildSearchIndex() {
        if (!this.hasItemTarget) return null;

        const items = this.itemTargets.map((el) => ({ value: el.dataset.value, element: el }));
        return new Fuse(items, { keys: ["value"], threshold: 0.2, includeMatches: true });
    }

    handleKeydown(e) {
        if (!this.hasItemTarget) return;

        const visible = this.itemTargets.filter((i) => !i.classList.contains("tw-hidden"));
        if (!visible.length) return;

        switch (e.key) {
            case "ArrowDown":
                e.preventDefault();
                this.updateSelectedItem(visible, 1);
                break;
            case "ArrowUp":
                e.preventDefault();
                this.updateSelectedItem(visible, -1);
                break;
            case "Enter":
                if (this.selectedIndex !== -1) {
                    e.preventDefault();
                    visible[this.selectedIndex].click();
                }
                break;
        }
    }

    updateSelectedItem(visible, direction) {
        if (this.selectedIndex >= 0) this.toggleAriaSelected(visible[this.selectedIndex], false);
        this.selectedIndex = (this.selectedIndex + direction + visible.length) % visible.length;
        this.toggleAriaSelected(visible[this.selectedIndex], true);
    }

    toggleAriaSelected(el, sel) {
        el.setAttribute("aria-selected", sel.toString());
    }

    deselectAll() {
        if (!this.hasItemTarget) return;

        this.itemTargets.forEach((i) => this.toggleAriaSelected(i, false));
        this.selectedIndex = -1;
    }
}